import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { auth, db } from '@/data/services/firebase';
import type { JsonObject } from '@/data/types/json';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * CampaignsDataService
 *
 * Feature-specific Firestore reads for campaigns and the six campaign ranking
 * queries. Mirrors Flutter's CampaignsDataSource
 * (lib/data/data_sources/campaigns_data_source.dart) collection paths, where
 * predicates, orderBy chains, and limits exactly.
 *
 * Per the data-layer surface rule (data-layer.md), every public method returns
 * Promise<JsonObject[]> — no Firestore types (DocumentData /
 * QueryDocumentSnapshot) leak through. Each method catches and returns [] on
 * error, matching Flutter's catch-and-return-empty behaviour.
 */
export class CampaignsDataService {
  private static instance: CampaignsDataService;

  private constructor() { }

  public static getInstance(): CampaignsDataService {
    if (!CampaignsDataService.instance) {
      CampaignsDataService.instance = new CampaignsDataService();
    }
    return CampaignsDataService.instance;
  }

  /**
   * Active campaigns whose startDate is at or before now.
   * Collection: campaigns
   * Filter:   where('startDate', '<=', now)   // isLessThanOrEqualTo, NOT '<'
   * Ordering: orderBy('startDate', 'desc')
   *
   * Mirrors CampaignsDataSource.getActiveCampaigns
   * (campaigns_data_source.dart:56-80). The campaign's own `id` field is stored
   * in the document (campaign.dart:8 `late String id`); spreading doc.data()
   * after doc.id lets the in-document `id` win, matching Flutter's
   * Campaign.fromJson(json.data()).
   *
   * Guards on a signed-in Firebase user before reading, mirroring Flutter's
   * `if (firebaseUser != null)` gate (campaigns_data_source.dart:58-59): returns
   * [] when no user is signed in. The auth.currentUser read matches the data-layer
   * precedent in cloudStorageService.uploadFileToUserProfilesBucket. Note Flutter
   * applies this guard to getActiveCampaigns only — the live ranking reads
   * (getCampaignUsersMostSubscribers, getCampaignContentMostLikes,
   * getCampaignContentMostCoins) query unconditionally, so they stay unguarded.
   */
  async getActiveCampaigns(): Promise<JsonObject[]> {
    try {
      if (!auth.currentUser) return [];

      const campaignsRef = collection(db, 'campaigns');
      const q = query(
        campaignsRef,
        where('startDate', '<=', Date.now()),
        orderBy('startDate', 'desc'),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      appLogger.error('Error fetching active campaigns', 'CampaignsDataService.getActiveCampaigns', { error: error as Error });
      return [];
    }
  }

  /**
   * Live user leaderboard ordered by subscribers.
   * Collection: users
   * Filter:   where('subscribers', '>=', 0)
   * Ordering: subscribers desc → subscription_charge desc → contentCount desc → likes desc
   * Limit:    limitCount (the campaign's rankingFetchLimit — a plain limit, not a
   *           where-in, so no FIRESTORE_WHERE_IN_MAX_VALUES cap applies)
   *
   * Mirrors CampaignsDataSource.getCampaignUsersMostSubscribers
   * (campaigns_data_source.dart:82-105).
   */
  async getCampaignUsersMostSubscribers(limitCount: number): Promise<JsonObject[]> {
    try {
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('subscribers', '>=', 0),
        orderBy('subscribers', 'desc'),
        orderBy('subscription_charge', 'desc'),
        orderBy('contentCount', 'desc'),
        orderBy('likes', 'desc'),
        limit(limitCount),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      appLogger.error('Error fetching campaign users most subscribers', 'CampaignsDataService.getCampaignUsersMostSubscribers', { error: error as Error });
      return [];
    }
  }

  /**
   * Ended-campaign user leaderboard from the frozen ranking subcollection.
   * Path:     campaigns/{campaignId}/ranking
   * Ordering: subscribers desc → contentCount desc → likes desc
   * Limit:    limitCount
   *
   * Mirrors CampaignsDataSource.getEndedCampaignUsersMostSubscribers
   * (campaigns_data_source.dart:107-131).
   */
  async getEndedCampaignUsersMostSubscribers(
    campaignId: string,
    limitCount: number,
  ): Promise<JsonObject[]> {
    try {
      const rankingRef = collection(db, 'campaigns', campaignId, 'ranking');
      const q = query(
        rankingRef,
        orderBy('subscribers', 'desc'),
        orderBy('contentCount', 'desc'),
        orderBy('likes', 'desc'),
        limit(limitCount),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      appLogger.error('Error fetching ended campaign users most subscribers', 'CampaignsDataService.getEndedCampaignUsersMostSubscribers', { error: error as Error });
      return [];
    }
  }

  /**
   * Live content leaderboard ordered by likes.
   * CollectionGroup: media
   * Filter:   where('campaigns', 'array-contains', campaignId)
   * Ordering: likes desc → coins desc → comments desc
   * Limit:    limitCount
   *
   * Mirrors CampaignsDataSource.getCampaignContentMostLikes
   * (campaigns_data_source.dart:133-158).
   */
  async getCampaignContentMostLikes(
    campaignId: string,
    limitCount: number,
  ): Promise<JsonObject[]> {
    try {
      const mediaRef = collectionGroup(db, 'media');
      const q = query(
        mediaRef,
        where('campaigns', 'array-contains', campaignId),
        orderBy('likes', 'desc'),
        orderBy('coins', 'desc'),
        orderBy('comments', 'desc'),
        limit(limitCount),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      appLogger.error('Error fetching campaign content most likes', 'CampaignsDataService.getCampaignContentMostLikes', { error: error as Error });
      return [];
    }
  }

  /**
   * Ended-campaign content leaderboard (likes) from the frozen ranking subcollection.
   * Path:     campaigns/{campaignId}/ranking
   * Ordering: likes desc → coins desc → comments desc
   * Limit:    limitCount
   *
   * Mirrors CampaignsDataSource.getEndedCampaignContentMostLikes
   * (campaigns_data_source.dart:160-185).
   */
  async getEndedCampaignContentMostLikes(
    campaignId: string,
    limitCount: number,
  ): Promise<JsonObject[]> {
    try {
      const rankingRef = collection(db, 'campaigns', campaignId, 'ranking');
      const q = query(
        rankingRef,
        orderBy('likes', 'desc'),
        orderBy('coins', 'desc'),
        orderBy('comments', 'desc'),
        limit(limitCount),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      appLogger.error('Error fetching ended campaign content most likes', 'CampaignsDataService.getEndedCampaignContentMostLikes', { error: error as Error });
      return [];
    }
  }

  /**
   * Live content leaderboard ordered by coins.
   * CollectionGroup: media
   * Filter:   where('campaigns', 'array-contains', campaignId)
   * Ordering: coins desc → likes desc → comments desc
   * Limit:    limitCount
   *
   * Mirrors CampaignsDataSource.getCampaignContentMostCoins
   * (campaigns_data_source.dart:187-212).
   */
  async getCampaignContentMostCoins(
    campaignId: string,
    limitCount: number,
  ): Promise<JsonObject[]> {
    try {
      const mediaRef = collectionGroup(db, 'media');
      const q = query(
        mediaRef,
        where('campaigns', 'array-contains', campaignId),
        orderBy('coins', 'desc'),
        orderBy('likes', 'desc'),
        orderBy('comments', 'desc'),
        limit(limitCount),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      appLogger.error('Error fetching campaign content most coins', 'CampaignsDataService.getCampaignContentMostCoins', { error: error as Error });
      return [];
    }
  }

  /**
   * Ended-campaign content leaderboard (coins) from the frozen ranking subcollection.
   * Path:     campaigns/{campaignId}/ranking
   * Ordering: coins desc → likes desc → comments desc
   * Limit:    limitCount
   *
   * Mirrors CampaignsDataSource.getEndedCampaignContentMostCoins
   * (campaigns_data_source.dart:214-239).
   */
  async getEndedCampaignContentMostCoins(
    campaignId: string,
    limitCount: number,
  ): Promise<JsonObject[]> {
    try {
      const rankingRef = collection(db, 'campaigns', campaignId, 'ranking');
      const q = query(
        rankingRef,
        orderBy('coins', 'desc'),
        orderBy('likes', 'desc'),
        orderBy('comments', 'desc'),
        limit(limitCount),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      appLogger.error('Error fetching ended campaign content most coins', 'CampaignsDataService.getEndedCampaignContentMostCoins', { error: error as Error });
      return [];
    }
  }
}

export const campaignsDataService = CampaignsDataService.getInstance();
